/**
 * API client for Families endpoints
 */

import apiClient from "./client";
import type {
  Family,
  FamilyDetail,
  FamilyListResponse,
  CreateFamilyRequest,
  UpdateFamilyRequest,
  AddGuardianToFamilyRequest,
  UpdateFamilyGuardianRequest,
  ListFamiliesParams,
  MessageResponse,
} from "@/types/family";

/**
 * Families API endpoints
 */
export const familiesApi = {
  /**
   * List families with pagination and filters
   */
  list: (params?: ListFamiliesParams) =>
    apiClient.get<FamilyListResponse>("/api/families/v1/", { params }),

  /**
   * Get family by ID with details (includes guardians and students)
   */
  get: (id: string) => apiClient.get<FamilyDetail>(`/api/families/v1/${id}`),

  /**
   * Create new family
   */
  create: (data: CreateFamilyRequest) =>
    apiClient.post<Family>("/api/families/v1/", data),

  /**
   * Update family
   */
  update: (id: string, data: UpdateFamilyRequest) =>
    apiClient.patch<Family>(`/api/families/v1/${id}`, data),

  /**
   * Archive family (soft delete)
   */
  delete: (id: string) =>
    apiClient.delete<MessageResponse>(`/api/families/v1/${id}`),

  /**
   * Merge a duplicate family into this canonical family. Re-parents all
   * children (students, guardians, invoices, payments, etc.) and archives
   * the duplicate. Admin-only, multi-tenant safe.
   */
  merge: (canonicalId: string, duplicateFamilyId: string) =>
    apiClient.post<{
      canonical_family_id: string;
      duplicate_family_id: string;
      moved: Record<string, number>;
    }>(`/api/families/v1/${canonicalId}/merge`, {
      duplicate_family_id: duplicateFamilyId,
    }),

  /**
   * Add guardian to family
   */
  addGuardian: (familyId: string, data: AddGuardianToFamilyRequest) =>
    apiClient.post<MessageResponse>(
      `/api/families/v1/${familyId}/guardians`,
      data,
    ),

  /**
   * Update family-guardian relationship
   */
  updateGuardian: (
    familyId: string,
    guardianId: string,
    data: UpdateFamilyGuardianRequest,
  ) =>
    apiClient.patch<MessageResponse>(
      `/api/families/v1/${familyId}/guardians/${guardianId}`,
      data,
    ),

  /**
   * Remove guardian from family
   */
  removeGuardian: (familyId: string, guardianId: string) =>
    apiClient.delete<MessageResponse>(
      `/api/families/v1/${familyId}/guardians/${guardianId}`,
    ),

  /**
   * Export all families (fetches all records for CSV export)
   */
  exportAll: async (params?: Omit<ListFamiliesParams, 'page' | 'limit'>) => {
    const allFamilies: Family[] = [];
    let page = 1;
    const limit = 100;
    let hasMore = true;

    while (hasMore) {
      const response = await apiClient.get<FamilyListResponse>("/api/families/v1/", {
        params: { ...params, page, limit },
      });
      const data = response.data;
      allFamilies.push(...data.families);

      hasMore = page < data.meta.total_pages;
      page++;
    }

    return { families: allFamilies, meta: { total: allFamilies.length, page: 1, limit: allFamilies.length, total_pages: 1 } };
  },
};

export default familiesApi;
